const STORAGE_KEY = 'shapes-td.tutorial.v1'

export type TutorialStatus = 'active' | 'completed' | 'skipped'

export type TutorialStepId =
  | 'build'
  | 'startWave'
  | 'pathInfo'
  | 'upgrade'
  | 'targeting'
  | 'controlHint'
  | 'done'

export interface TutorialPersistedState {
  status: TutorialStatus
  step: TutorialStepId
  /** Families already introduced via one-line chips (session + persisted). */
  seenEnemyFamilies: string[]
  /** Mechanic hints already shown (aa, mage, …). */
  seenMechanicHints: string[]
}

export const TUTORIAL_STEPS: TutorialStepId[] = [
  'build',
  'startWave',
  'pathInfo',
  'upgrade',
  'targeting',
  'controlHint',
  'done'
]

export const ENEMY_INTRO_COPY: Record<string, string> = {
  fast: 'FAST — quick, low health. Can overtake slower enemies.',
  tank: 'TANK — slow, much more health.',
  regen: 'REGEN — heals after 2s without taking damage.',
  shield: 'SHIELD — damage the shield before health.',
  flyer: 'FLYER — AIR route. Cannon cannot target air.',
  air_boss: 'AIR BOSS — heavy air threat. Needs AA coverage.',
  boss: 'BOSS — high health. Leak damage: 10.'
}

/** One-shot mechanic hints keyed by stage introduction (not global spam). */
export const MECHANIC_HINT_COPY: Record<string, string> = {
  aa: 'AA only hits AIR. Cannon cannot. Fire/Frost/Poison cover both.',
  mage: 'Mage applies Armor Break — other towers deal more to armored foes.'
}

const DEFAULT_STATE: TutorialPersistedState = {
  status: 'active',
  step: 'build',
  seenEnemyFamilies: [],
  seenMechanicHints: []
}

function isTutorialState(value: unknown): value is TutorialPersistedState {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<TutorialPersistedState>
  return (
    (record.status === 'active' ||
      record.status === 'completed' ||
      record.status === 'skipped') &&
    typeof record.step === 'string' &&
    Array.isArray(record.seenEnemyFamilies)
  )
}

export function createTutorialStorage(storage?: Storage) {
  const resolve = (): Storage | null => {
    if (storage) return storage
    if (typeof localStorage === 'undefined') return null
    return localStorage
  }

  return {
    load(): TutorialPersistedState {
      const backend = resolve()
      if (!backend) return { ...DEFAULT_STATE, seenEnemyFamilies: [], seenMechanicHints: [] }
      try {
        const raw = backend.getItem(STORAGE_KEY)
        if (!raw) return { ...DEFAULT_STATE, seenEnemyFamilies: [], seenMechanicHints: [] }
        const parsed = JSON.parse(raw) as unknown
        if (!isTutorialState(parsed)) {
          return { ...DEFAULT_STATE, seenEnemyFamilies: [], seenMechanicHints: [] }
        }
        return {
          status: parsed.status,
          step: (TUTORIAL_STEPS.includes(parsed.step as TutorialStepId)
            ? parsed.step
            : 'build') as TutorialStepId,
          seenEnemyFamilies: parsed.seenEnemyFamilies.filter((id) => typeof id === 'string'),
          seenMechanicHints: Array.isArray(parsed.seenMechanicHints)
            ? parsed.seenMechanicHints.filter((id): id is string => typeof id === 'string')
            : []
        }
      } catch {
        return { ...DEFAULT_STATE, seenEnemyFamilies: [], seenMechanicHints: [] }
      }
    },

    save(state: TutorialPersistedState): void {
      const backend = resolve()
      if (!backend) return
      backend.setItem(STORAGE_KEY, JSON.stringify(state))
    },

    clear(): void {
      const backend = resolve()
      if (!backend) return
      backend.removeItem(STORAGE_KEY)
    }
  }
}

export const tutorialStorage = createTutorialStorage()
